/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton } from '@fluentui/react/lib/Button';
import { Icon, IIconStyleProps, IIconStyles } from '@fluentui/react/lib/Icon';
import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ErrorTextContainer } from 'src/core/components/ErrorTextContainer';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import { DocumentLoadingView, DocumentView } from 'src/customText/appShell/dataTaggingPage/components/DocumentView';
import { useFetchDocument } from 'src/customText/appShell/dataTaggingPage/hooks/useFetchDocument';
import { CustomTextDocumentMetadata } from 'src/customText/core/stores/CustomTextDocumentStore';
import { AutotagTriggerContentLeftMargin } from 'src/customText/verticals/autotagTriggerModal/autotagTriggerModal';
import { LabelingMode } from 'src/customText/verticals/extractionLabeler/ExtractionLabeler';

const messages = defineMessages({
    notFoundFileTitle: {
        id: 'customText.viewFileScreen.notFoundFileTitle',
        defaultMessage: "Document couldn't be found."
    },
    notFoundFileSubTitle: {
        id: 'customText.viewFileScreen.notFoundFileSubTitle',
        defaultMessage: "Document {name} couldn't be found."
    },
    preview: {
        id: 'customText.viewFileScreen.preview',
        defaultMessage: 'Preview of'
    },
    cultureDropdownAriaLabel: {
        id: 'customText.viewFileScreen.cultureDropdownAriaLabel',
        defaultMessage: 'culture Dropdown'
    }
});

export type AutotagTriggerModalViewFileProps = {
    document: CustomTextDocumentMetadata;
    onDismiss: () => void;
};

const { styleComponent } = getStylistV2('ViewFileScreen');
const modalTheme = getScopedTheme('AutotagTriggerModalBase');
const backIconThem = getScopedTheme('Arrow');
const borderStyle = `solid 1px ${modalTheme.borderColor}`;
const Wrapper = styleComponent<IStackProps>(Stack)('Wrapper', () => ({
    borderTop: borderStyle,
    position: 'absolute',
    bottom: '0px',
    width: '100%'
}));
const iconStyles = classNamesFunction<IIconStyleProps, IIconStyles>()({
    root: {
        marginTop: '7px',
        marginBottom: '2px',
        fontSize: 20,
        cursor: 'pointer',
        lineHeight: '14px',
        color: backIconThem.buttonColor,
        borderRadius: backIconThem.buttonBorderRadius,
        '&:hover': { backgroundColor: backIconThem.hoveredButtonBackground, color: backIconThem.buttonColor }
    }
});

export const ViewFileScreen = (props: AutotagTriggerModalViewFileProps) => {
    const { document, onDismiss } = props;
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();

    const [labelingMode] = React.useState<LabelingMode>({ mode: 'menu' });

    const getSpecificFileTracker = useProgressTracker({
        persistOnComplete: true
    });

    const { currentDocumentStore, culture: currentProjectCulture } = rootStore.currentCustomTextProjectStore;

    const { isLoading: ifDocumentContentFetchingLoading, isFailed: isFetchingDocumentFailed } = useFetchDocument(document.name);

    const isCurrentDocumentLoading =
        ifDocumentContentFetchingLoading || (currentDocumentStore && currentDocumentStore?.name !== document.name);

    const [documentCulture, setDocumentCulture] = React.useState(currentProjectCulture);

    const documentView = (() => {
        if (isFetchingDocumentFailed) {
            return (
                <ErrorTextContainer
                    title={localize(messages.notFoundFileTitle)}
                    subTitle={localize(messages.notFoundFileSubTitle, { name: document.name })}
                />
            );
        }

        if (getSpecificFileTracker.isRunning) {
            return <DocumentLoadingView />;
        }

        return (
            <DocumentView
                labelingMode={labelingMode}
                isExtractionLabelerDisabled
                documentCulture={documentCulture}
                isDocumentLoading={isCurrentDocumentLoading}
                labelingHistoryStacks={{ undo: [], redo: [] }}
            />
        );
    })();

    return (
        <Stack>
            <Stack
                styles={{ root: { height: '50px', overflowY: 'auto' } }}
                tokens={{ childrenGap: 24, padding: `0 0 0 ${AutotagTriggerContentLeftMargin}` }}>
                <Stack horizontal styles={{ root: { width: '100%' } }} verticalAlign="center" horizontalAlign="space-between">
                    <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 20 }}>
                        <Icon iconName="Back" styles={iconStyles} onClick={onDismiss} />
                        <TruncatedText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                            {currentDocumentStore && `${localize(messages.preview)} ${currentDocumentStore.name}`}
                        </TruncatedText>
                        {rootStore.currentCustomTextProjectStore.multiLingual && (
                            <LuisDropdown
                                key={document.language}
                                ariaLabel={localize(messages.cultureDropdownAriaLabel)}
                                styles={{ root: { minWidth: '100px', marginTop: '7px' } }}
                                disabled={rootStore.customTextCultureStorehouse.tracker.isRunning}
                                onChange={(_, option) => {
                                    setDocumentCulture(option.key as string);
                                    document.language = option.key as string;
                                }}
                                options={rootStore.customTextCultureStorehouse.items.map(culture => ({
                                    key: culture.code,
                                    text: culture.name,
                                    selected: culture.code === document.language
                                }))}
                            />
                        )}
                    </Stack>
                </Stack>
            </Stack>
            <Stack
                styles={{ root: { minHeight: '450px', height: '100%', position: 'relative' } }}
                tokens={{ childrenGap: 24, padding: `0 0 0 ${AutotagTriggerContentLeftMargin}` }}>
                {documentView}
            </Stack>
            <Wrapper horizontal horizontalAlign="end" tokens={{ padding: '16px 0px' }}>
                <DefaultButton onClick={onDismiss}>Close file</DefaultButton>
            </Wrapper>
        </Stack>
    );
};
